import { useState } from "react";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

const loadCheckout = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export const PayFeeButton = ({ invoice, onPaid, enabled }) => {
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/pay/order`);
      const ok = await loadCheckout();
      if (!ok) throw new Error("Could not load the payment window. Check your connection.");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "AdhocArb Registry",
        description: data.routed
          ? `Fee note ${data.invoice_number} — paid directly to the tribunal`
          : `Fee note ${data.invoice_number}`,
        order_id: data.order_id,
        prefill: { name: data.name, email: data.email, contact: data.contact },
        theme: { color: "#1E3A8A" },
        handler: async (res) => {
          try {
            await api.post(`/invoices/${invoice.id}/pay/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(data.routed
              ? "Payment received — the tribunal's fee has been routed to their own account"
              : "Payment received — fee note settled");
            onPaid?.();
          } catch (e) {
            toast.error(errMsg(e));
          }
        },
        modal: { ondismiss: () => toast.info("Payment window closed") },
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) {
    return (
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground" data-testid={`pay-disabled-${invoice.id}`}>
        Online payment not yet enabled
      </span>
    );
  }

  return (
    <Button data-testid={`pay-invoice-${invoice.id}`} onClick={pay} disabled={busy}
      className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-9 px-4">
      <CreditCard className="h-3.5 w-3.5 mr-2" />
      {busy ? "Opening…" : `Pay ${money(invoice.amount, invoice.currency)}`}
    </Button>
  );
};

export default PayFeeButton;
